import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  addDoc,
  serverTimestamp,
  query,
  where,
} from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { db } from "../../firebaseConfig";
import AccountingShell from "./AccountingShell";
import toast from "react-hot-toast";

const GST_RATE = 18;
const DEFAULT_TOTAL = 2950;

const INR = (n) =>
  `₹ ${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const splitGST = (gross = DEFAULT_TOTAL) => {
  const total = Number(gross || DEFAULT_TOTAL);
  const taxable = Math.round((total * 100) / 118);
  const gst = total - taxable;
  return { taxable, cgst: gst / 2, sgst: gst / 2, gst, gross: total };
};

const toDateInput = (v) => {
  try {
    const d = v?.toDate ? v.toDate() : v ? new Date(v) : new Date();
    if (!d || Number.isNaN(d.getTime())) return "";
    return d.toISOString().slice(0, 10);
  } catch {
    return "";
  }
};

const getFY = (dateValue) => {
  const d = dateValue ? new Date(dateValue) : new Date();
  const y = d.getFullYear();
  const start = d.getMonth() + 1 >= 4 ? y : y - 1;
  return `${String(start).slice(-2)}${String(start + 1).slice(-2)}`;
};

const makeInvoiceNo = () => `REG/${getFY()}/${String(Date.now()).slice(-6)}`;
const makeReceiptNo = () => `RCP/${getFY()}/${String(Date.now()).slice(-6)}`;

const fmtDate = (v) => {
  try {
    const d = v?.toDate ? v.toDate() : v ? new Date(v) : null;
    return d && !Number.isNaN(d.getTime())
      ? d.toLocaleDateString("en-IN")
      : "—";
  } catch {
    return "—";
  }
};

const getTime = (v) => {
  try {
    const d = v?.toDate ? v.toDate() : v ? new Date(v) : null;
    return d && !Number.isNaN(d.getTime()) ? d.getTime() : 0;
  } catch {
    return 0;
  }
};

const getVendorName = (v) =>
  v.profile?.companyName ||
  v.companyName ||
  v.firmName ||
  v.name ||
  v.vendorName ||
  v.id ||
  "—";

const getEmail = (v) =>
  v.auth?.email || v.profile?.email || v.email || v.contactEmail || "—";

const getMobile = (v) =>
  v.profile?.mobile || v.mobile || v.phone || v.contactMobile || "—";

const getRegAmount = (v) =>
  Number(
    v.fees?.registration?.paidAmount ||
      v.registrationFeeAmount ||
      v.registrationAmount ||
      v.registrationGrossAmount ||
      DEFAULT_TOTAL
  );

const getReceiptUrl = (v) =>
  v.fees?.registration?.receiptUrl ||
  v.registrationReceiptUrl ||
  v.receiptUrl ||
  v.paymentReceiptUrl ||
  v.registrationPayment?.receiptUrl ||
  "";

function Badge({ children }) {
  const s = String(children || "").toUpperCase();
  let cls = "bg-amber-100 text-amber-700";

  if (s.includes("APPROVED") || s.includes("POSTED") || s.includes("VERIFIED"))
    cls = "bg-emerald-100 text-emerald-700";
  if (s.includes("INVOICE_GENERATED") || s.includes("OLD_INVOICE"))
    cls = "bg-purple-100 text-purple-700";
  if (s.includes("ACCOUNTANT")) cls = "bg-blue-100 text-blue-700";
  if (s.includes("CORRECTION") || s.includes("REJECT"))
    cls = "bg-red-100 text-red-700";

  return (
    <span className={`inline-flex rounded-full px-3 py-1 text-[10px] font-black ${cls}`}>
      {children || "—"}
    </span>
  );
}

function generateInvoicePdfBlob(v, invoice) {
  const pdf = new jsPDF("p", "mm", "a4");
  const pageW = pdf.internal.pageSize.getWidth();

  pdf.setFillColor(15, 23, 42);
  pdf.rect(0, 0, pageW, 22, "F");
  pdf.setTextColor(255, 255, 255);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(12);
  pdf.text(
    "SHINE AGRI ALLIED & MARKETING MULTISTATE COOPERATIVE SOCIETY LTD.",
    pageW / 2,
    9,
    { align: "center" }
  );
  pdf.setFontSize(8);
  pdf.text("GST TAX INVOICE - Vendor Registration Fee", pageW / 2, 16, {
    align: "center",
  });

  pdf.setTextColor(0, 0, 0);
  pdf.setFontSize(16);
  pdf.text("GST TAX INVOICE", pageW / 2, 34, { align: "center" });

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(9);
  pdf.text(`Invoice No: ${invoice.invoiceNo}`, 14, 48);
  pdf.text(`Invoice Date: ${fmtDate(invoice.invoiceDate)}`, pageW - 14, 48, {
    align: "right",
  });

  pdf.setFont("helvetica", "bold");
  pdf.text("Bill To", 14, 60);
  pdf.setFont("helvetica", "normal");
  pdf.text(`Name: ${v.vendorDisplay}`, 14, 67);
  pdf.text(`Email: ${v.emailDisplay}`, 14, 73);
  pdf.text(`Mobile: ${v.mobileDisplay}`, 14, 79);
  pdf.text(`GST No: ${v.gstNo || v.profile?.gstNo || "—"}`, 14, 85);
  pdf.text(`PAN No: ${v.panNo || v.profile?.panNo || "—"}`, 14, 91);

  autoTable(pdf, {
    startY: 102,
    theme: "grid",
    head: [["Sr", "Description", "HSN/SAC", "Taxable", "CGST", "SGST", "Total"]],
    body: [
      [
        1,
        "One Time Vendor Registration Fee",
        "998599",
        invoice.taxable.toFixed(2),
        invoice.cgst.toFixed(2),
        invoice.sgst.toFixed(2),
        invoice.gross.toFixed(2),
      ],
    ],
    headStyles: { fillColor: [15, 23, 42], textColor: [255, 255, 255] },
    styles: { fontSize: 9, cellPadding: 2 },
  });

  autoTable(pdf, {
    startY: pdf.lastAutoTable.finalY + 8,
    margin: { left: pageW - 95, right: 14 },
    theme: "grid",
    body: [
      ["Taxable Amount", `Rs. ${invoice.taxable.toLocaleString("en-IN")}`],
      ["CGST", `Rs. ${invoice.cgst.toLocaleString("en-IN")}`],
      ["SGST", `Rs. ${invoice.sgst.toLocaleString("en-IN")}`],
      ["Grand Total", `Rs. ${invoice.gross.toLocaleString("en-IN")}`],
    ],
    styles: { fontSize: 9, cellPadding: 2 },
  });

  return pdf.output("blob");
}

function generatePaymentReceiptPdfBlob(v, receipt) {
  const pdf = new jsPDF("p", "mm", "a4");
  const pageW = pdf.internal.pageSize.getWidth();

  pdf.setFillColor(15, 23, 42);
  pdf.rect(0, 0, pageW, 22, "F");
  pdf.setTextColor(255, 255, 255);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(12);
  pdf.text(
    "SHINE AGRI ALLIED & MARKETING MULTISTATE COOPERATIVE SOCIETY LTD.",
    pageW / 2,
    9,
    { align: "center" }
  );
  pdf.setFontSize(8);
  pdf.text("PAYMENT RECEIPT - Vendor Registration Fee", pageW / 2, 16, {
    align: "center",
  });

  pdf.setTextColor(0, 0, 0);
  pdf.setFontSize(16);
  pdf.text("PAYMENT RECEIPT", pageW / 2, 34, { align: "center" });

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(9);
  pdf.text(`Receipt No: ${receipt.receiptNo}`, 14, 48);
  pdf.text(`Receipt Date: ${fmtDate(receipt.receiptDate)}`, pageW - 14, 48, {
    align: "right",
  });

  autoTable(pdf, {
    startY: 60,
    theme: "grid",
    head: [["Particular", "Detail"]],
    body: [
      ["Vendor", v.vendorDisplay],
      ["Email", v.emailDisplay],
      ["Mobile", v.mobileDisplay],
      ["Against Invoice", v.registrationInvoiceNo || "—"],
      ["Payment UTR / Ref", receipt.utr],
      ["Amount Received", `Rs. ${Number(receipt.amount || 0).toLocaleString("en-IN")}`],
      ["Narration", `Payment received against registration invoice ${v.registrationInvoiceNo}`],
    ],
    headStyles: { fillColor: [15, 23, 42], textColor: [255, 255, 255] },
    styles: { fontSize: 9, cellPadding: 3 },
  });

  pdf.setFont("helvetica", "bold");
  pdf.text("For Shine Agri Allied & Marketing MSCS Ltd.", pageW - 14, 160, {
    align: "right",
  });
  pdf.text("Authorized Signatory", pageW - 14, 176, { align: "right" });

  return pdf.output("blob");
}

export default function RegistrationVerify() {
  return (
    <AccountingShell title="Registration Fee Verification">
      <RegistrationVerifyContent />
    </AccountingShell>
  );
}

function RegistrationVerifyContent({ me = {} }) {
  const [vendors, setVendors] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [paymentFilter, setPaymentFilter] = useState("ALL");
  const [fyFilter, setFyFilter] = useState("ALL");
  const [sortBy, setSortBy] = useState("NEWEST");

  const load = async () => {
    const snap = await getDocs(collection(db, "vendors"));
    setVendors(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  };

  useEffect(() => {
    load();
  }, []);

  const rows = useMemo(() => {
    let list = vendors.map((v) => {
      const amount = getRegAmount(v);
      const gstSplit = splitGST(amount);
      const receiptUrl = getReceiptUrl(v);

      const invoiceNo = v.registrationInvoiceNo || v.oldRegistrationInvoiceNo || "";
      const invoiceDate =
        v.registrationInvoiceDate ||
        v.oldRegistrationInvoiceDate ||
        v.registrationInvoiceGeneratedAt ||
        v.createdAt ||
        v.registeredAt ||
        v.appliedAt;

      const invoiceFY = v.registrationInvoiceFY || getFY(toDateInput(invoiceDate));
      const invoiceDebit = Number(v.registrationInvoiceDebit || 0);
      const paymentCredit = Number(v.registrationPaymentCredit || 0);

      return {
        ...v,
        vendorDisplay: getVendorName(v),
        emailDisplay: getEmail(v),
        mobileDisplay: getMobile(v),
        amount,
        taxableAmount: Number(v.registrationTaxableAmount || gstSplit.taxable),
        cgstAmount: Number(v.registrationCgstAmount || gstSplit.cgst),
        sgstAmount: Number(v.registrationSgstAmount || gstSplit.sgst),
        gstAmount: Number(v.registrationGstAmount || gstSplit.gst),
        grossAmount: Number(v.registrationGrossAmount || gstSplit.gross),
        receiptUrl,
        hasReceipt: Boolean(receiptUrl),
        registrationInvoiceNo: invoiceNo,
        registrationInvoiceDate: invoiceDate,
        registrationInvoicePdfUrl:
          v.registrationInvoicePdfUrl || v.oldRegistrationInvoicePdfUrl || "",
        registrationPaymentReceiptPdfUrl: v.registrationPaymentReceiptPdfUrl || "",
        hasInvoice: Boolean(invoiceNo),
        invoiceFY,
        oldInvoiceVerified: Boolean(v.registrationOldInvoiceVerified),
        accountantVerified: Boolean(v.registrationAccountantVerified),
        ceoVerified: v.registrationInvoiceStatus === "CEO_APPROVED",
        status: String(v.registrationFeeStatus || v.status || "PENDING").toUpperCase(),
        invoiceStatus: String(
          v.registrationInvoiceStatus ||
            (invoiceNo ? "OLD_INVOICE_PENDING_VERIFY" : "NOT_GENERATED")
        ).toUpperCase(),
        paymentStatus: String(v.registrationPaymentStatus || "PENDING").toUpperCase(),
        invoiceDebit,
        paymentCredit,
        balance: invoiceDebit - paymentCredit,
        createdTime: getTime(v.createdAt || v.registeredAt || v.appliedAt),
      };
    });

    if (fyFilter !== "ALL") list = list.filter((v) => v.invoiceFY === fyFilter);

    if (statusFilter !== "ALL") {
      list = list.filter(
        (v) =>
          v.status === statusFilter ||
          v.invoiceStatus === statusFilter ||
          v.paymentStatus === statusFilter
      );
    }

    if (paymentFilter === "OLD_INVOICE")
      list = list.filter((v) => v.hasInvoice && !v.oldInvoiceVerified);
    if (paymentFilter === "ACCOUNTANT_PENDING")
      list = list.filter((v) => !v.accountantVerified);
    if (paymentFilter === "CEO_PENDING")
      list = list.filter((v) => v.accountantVerified && !v.ceoVerified);
    if (paymentFilter === "INVOICE_MISSING")
      list = list.filter((v) => !v.hasInvoice);
    if (paymentFilter === "PAYMENT_PENDING")
      list = list.filter((v) => v.ceoVerified && v.paymentStatus !== "PAYMENT_VERIFIED");
    if (paymentFilter === "BALANCE_PENDING")
      list = list.filter((v) => v.balance > 0);
    if (paymentFilter === "BALANCE_CLEAR")
      list = list.filter((v) => v.balance === 0 && v.invoiceDebit > 0);

    if (search.trim()) {
      const s = search.toLowerCase();
      list = list.filter((v) =>
        [
          v.vendorDisplay,
          v.emailDisplay,
          v.mobileDisplay,
          v.id,
          v.vendorId,
          v.registrationInvoiceNo,
          v.registrationPaymentUtr,
          v.registrationPaymentReceiptNo,
          v.registrationAccountingRemark,
        ]
          .join(" ")
          .toLowerCase()
          .includes(s)
      );
    }

    if (sortBy === "NEWEST") list.sort((a, b) => b.createdTime - a.createdTime);
    if (sortBy === "OLDEST") list.sort((a, b) => a.createdTime - b.createdTime);
    if (sortBy === "AMOUNT_HIGH") list.sort((a, b) => b.amount - a.amount);

    return list;
  }, [vendors, search, statusFilter, paymentFilter, fyFilter, sortBy]);

  const fyOptions = useMemo(() => {
    const set = new Set(
      vendors
        .map((v) =>
          getFY(
            toDateInput(
              v.registrationInvoiceDate ||
                v.oldRegistrationInvoiceDate ||
                v.createdAt ||
                v.registeredAt
            )
          )
        )
        .filter(Boolean)
    );
    return ["ALL", ...Array.from(set).sort().reverse()];
  }, [vendors]);

  const totals = useMemo(
    () => ({
      total: rows.length,
      invoices: rows.filter((v) => v.hasInvoice).length,
      accountantVerified: rows.filter((v) => v.accountantVerified).length,
      ceoVerified: rows.filter((v) => v.ceoVerified).length,
      gross: rows.reduce((s, v) => s + Number(v.amount || 0), 0),
      debit: rows.reduce((s, v) => s + Number(v.invoiceDebit || 0), 0),
      credit: rows.reduce((s, v) => s + Number(v.paymentCredit || 0), 0),
      balance: rows.reduce((s, v) => s + Number(v.balance || 0), 0),
    }),
    [rows]
  );

  const safeAddLedger = async (payload) => {
    const q = query(
      collection(db, "contractor_ledgers"),
      where("vendorUid", "==", payload.vendorUid),
      where("type", "==", payload.type),
      where("voucherNo", "==", payload.voucherNo)
    );
    const snap = await getDocs(q);

    if (!snap.empty) return false;

    await addDoc(collection(db, "contractor_ledgers"), {
      ...payload,
      createdAt: serverTimestamp(),
      createdBy: me.uid || "",
      createdByRole: me.role || "",
    });

    return true;
  };

  const uploadBlob = async (path, blob) => {
    const storage = getStorage();
    const fileRef = ref(storage, path);
    await uploadBytes(fileRef, blob, { contentType: "application/pdf" });
    return getDownloadURL(fileRef);
  };

  const editInvoiceDetails = async (v) => {
    const invoiceNo = window.prompt("Invoice Number:", v.registrationInvoiceNo || "");
    if (!invoiceNo) return;

    const invoiceDate = window.prompt(
      "Invoice Date YYYY-MM-DD:",
      toDateInput(v.registrationInvoiceDate)
    );
    if (!invoiceDate) return;

    const amountText = window.prompt("Gross Amount:", String(v.amount || DEFAULT_TOTAL));
    if (!amountText) return;

    const amount = Number(amountText);
    const split = splitGST(amount);

    await updateDoc(doc(db, "vendors", v.id), {
      registrationInvoiceNo: invoiceNo,
      registrationInvoiceDate: invoiceDate,
      registrationInvoiceFY: getFY(invoiceDate),
      registrationTaxableAmount: split.taxable,
      registrationCgstAmount: split.cgst,
      registrationSgstAmount: split.sgst,
      registrationGstAmount: split.gst,
      registrationGrossAmount: split.gross,
      registrationInvoiceEditedBy: me.uid || "",
      registrationInvoiceEditedAt: serverTimestamp(),
    });

    toast.success("Invoice details updated.");
    load();
  };

  const sendCorrection = async (v) => {
    const remark = window.prompt("Correction remark likho:");
    if (!remark) return;

    await updateDoc(doc(db, "vendors", v.id), {
      registrationFeeStatus: "CORRECTION_REQUIRED",
      registrationAccountingRemark: remark,
      registrationCorrectionRaisedBy: me.uid || "",
      registrationCorrectionRaisedAt: serverTimestamp(),
    });

    toast.success("Correction sent");
    load();
  };

  const accountantGenerateInvoice = async (v) => {
    const invoiceNo =
      window.prompt("Invoice No:", v.registrationInvoiceNo || makeInvoiceNo()) ||
      makeInvoiceNo();

    const invoiceDate =
      window.prompt("Invoice Date YYYY-MM-DD:", toDateInput(v.registrationInvoiceDate)) ||
      toDateInput(new Date());

    const split = splitGST(Number(v.amount || DEFAULT_TOTAL));
    const pdfBlob = generateInvoicePdfBlob(v, { invoiceNo, invoiceDate, ...split });

    const safeNo = invoiceNo.replace(/[\/\\]/g, "-");
    const filePath = `registration_invoices/${v.id}/${safeNo}.pdf`;
    const pdfUrl = await uploadBlob(filePath, pdfBlob);

    await updateDoc(doc(db, "vendors", v.id), {
      registrationFeeStatus: "INVOICE_GENERATED",
      registrationInvoiceStatus: "PENDING_CEO_APPROVAL",
      registrationAccountantVerified: true,
      registrationAccountantVerifiedBy: me.uid || "",
      registrationAccountantVerifiedAt: serverTimestamp(),
      registrationInvoiceNo: invoiceNo,
      registrationInvoiceDate: invoiceDate,
      registrationInvoiceFY: getFY(invoiceDate),
      registrationInvoicePdfUrl: pdfUrl,
      registrationInvoicePdfPath: filePath,
      registrationTaxableAmount: split.taxable,
      registrationGstRate: GST_RATE,
      registrationCgstAmount: split.cgst,
      registrationSgstAmount: split.sgst,
      registrationGstAmount: split.gst,
      registrationGrossAmount: split.gross,
      registrationInvoiceGeneratedBy: me.uid || "",
      registrationInvoiceGeneratedAt: serverTimestamp(),
    });

    toast.success("Invoice generated. CEO approval pending.");
    load();
  };

  const verifyOldInvoice = async (v) => {
    const invoiceNo = v.registrationInvoiceNo || window.prompt("Old invoice no:", "");
    if (!invoiceNo) return;

    const invoiceDate =
      window.prompt("Old Invoice Date YYYY-MM-DD:", toDateInput(v.registrationInvoiceDate)) ||
      toDateInput(new Date());

    const amount = Number(v.registrationGrossAmount || v.amount || DEFAULT_TOTAL);
    const split = splitGST(amount);

    await updateDoc(doc(db, "vendors", v.id), {
      registrationFeeStatus: "INVOICE_GENERATED",
      registrationInvoiceStatus: "PENDING_CEO_APPROVAL",
      registrationInvoiceNo: invoiceNo,
      registrationInvoiceDate: invoiceDate,
      registrationInvoiceFY: getFY(invoiceDate),
      registrationTaxableAmount: split.taxable,
      registrationGstRate: GST_RATE,
      registrationCgstAmount: split.cgst,
      registrationSgstAmount: split.sgst,
      registrationGstAmount: split.gst,
      registrationGrossAmount: split.gross,
      registrationOldInvoiceVerified: true,
      registrationAccountantVerified: true,
      registrationAccountantVerifiedBy: me.uid || "",
      registrationAccountantVerifiedAt: serverTimestamp(),
      registrationOldInvoiceVerifiedBy: me.uid || "",
      registrationOldInvoiceVerifiedAt: serverTimestamp(),
    });

    toast.success("Old invoice verified. CEO pending.");
    load();
  };

  const ceoApproveInvoice = async (v) => {
    if (!v.registrationInvoiceNo) {
      return toast.error("Pehle invoice generate/verify karo.");
    }

    const amount = Number(v.registrationGrossAmount || v.amount || DEFAULT_TOTAL);
    const split = splitGST(amount);

    await updateDoc(doc(db, "vendors", v.id), {
      status: "approved",
      registrationFeeStatus: "APPROVED",
      registrationInvoiceStatus: "CEO_APPROVED",
      registrationCeoApprovedBy: me.uid || "",
      registrationCeoApprovedAt: serverTimestamp(),
      registrationInvoiceDebit: amount,
      registrationBalance: amount - Number(v.registrationPaymentCredit || 0),
      registrationFeeAccountingStatus: "INVOICE_LEDGER_POSTED",
    });

    await safeAddLedger({
      vendorUid: v.id,
      vendorName: v.vendorDisplay,
      voucherNo: v.registrationInvoiceNo,
      invoiceNo: v.registrationInvoiceNo,
      type: "REGISTRATION_GST_INVOICE",
      debit: amount,
      credit: 0,
      taxableAmount: split.taxable,
      cgstAmount: split.cgst,
      sgstAmount: split.sgst,
      gstAmount: split.gst,
      grossAmount: split.gross,
      invoicePdfUrl: v.registrationInvoicePdfUrl || "",
      narration: `Registration GST Invoice - ${v.registrationInvoiceNo}`,
      serviceName: "Vendor Registration Fee",
      status: "POSTED",
    });

    toast.success("CEO approved. Invoice posted in DEBIT.");
    load();
  };

  const accountantVerifyPayment = async (v) => {
    if (v.registrationInvoiceStatus !== "CEO_APPROVED") {
      return toast.error("Pehle CEO invoice approve kare.");
    }

    const utr = window.prompt(
      "Payment UTR / Reference No:",
      v.registrationPayment?.reference || v.registrationPaymentUtr || ""
    );
    if (!utr) return;

    const amount = Number(v.registrationGrossAmount || v.amount || DEFAULT_TOTAL);
    const receiptNo = v.registrationPaymentReceiptNo || makeReceiptNo();
    const receiptDate = toDateInput(new Date());

    const receiptBlob = generatePaymentReceiptPdfBlob(v, {
      receiptNo,
      receiptDate,
      utr,
      amount,
    });

    const safeNo = receiptNo.replace(/[\/\\]/g, "-");
    const receiptPath = `registration_payment_receipts/${v.id}/${safeNo}.pdf`;
    const receiptPdfUrl = await uploadBlob(receiptPath, receiptBlob);

    await updateDoc(doc(db, "vendors", v.id), {
      registrationPaymentStatus: "PAYMENT_VERIFIED",
      registrationPaymentVerifiedBy: me.uid || "",
      registrationPaymentVerifiedAt: serverTimestamp(),
      registrationPaymentUtr: utr,
      registrationPaymentCredit: amount,
      registrationBalance: 0,
      registrationPaymentReceiptNo: receiptNo,
      registrationPaymentReceiptDate: receiptDate,
      registrationPaymentReceiptPdfUrl: receiptPdfUrl,
      registrationPaymentReceiptPdfPath: receiptPath,
      registrationFeeAccountingStatus: "PAYMENT_LEDGER_POSTED",
    });

    await safeAddLedger({
      vendorUid: v.id,
      vendorName: v.vendorDisplay,
      voucherNo: `${v.registrationInvoiceNo}-PAY`,
      referenceNo: utr,
      type: "REGISTRATION_PAYMENT_RECEIVED",
      debit: 0,
      credit: amount,
      invoiceNo: v.registrationInvoiceNo,
      invoicePdfUrl: v.registrationInvoicePdfUrl || "",
      paymentReceiptNo: receiptNo,
      paymentReceiptPdfUrl: receiptPdfUrl,
      narration: `Payment received against invoice ${v.registrationInvoiceNo}`,
      paymentUtr: utr,
      status: "POSTED",
    });

    toast.success("Payment verified. Receipt generated + CREDIT ledger posted.");
    load();
  };

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 xl:grid-cols-8 gap-3 print:hidden">
        <Card title="Vendors" value={totals.total} />
        <Card title="Invoices" value={totals.invoices} green />
        <Card title="Accountant Verified" value={totals.accountantVerified} green />
        <Card title="CEO Approved" value={totals.ceoVerified} green />
        <Card title="Gross" value={INR(totals.gross)} />
        <Card title="Debit" value={INR(totals.debit)} red />
        <Card title="Credit" value={INR(totals.credit)} green />
        <Card
          title="Balance"
          value={INR(totals.balance)}
          red={totals.balance > 0}
          green={totals.balance === 0}
        />
      </div>

      <div className="bg-white rounded-2xl border p-4 print:hidden">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <input
            className="input"
            placeholder="Search vendor / invoice / receipt / UTR..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <select className="input" value={fyFilter} onChange={(e) => setFyFilter(e.target.value)}>
            {fyOptions.map((f) => (
              <option key={f} value={f}>
                {f === "ALL" ? "All FY" : `FY ${f}`}
              </option>
            ))}
          </select>

          <select className="input" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="ALL">All Status</option>
            <option value="OLD_INVOICE_PENDING_VERIFY">Old Invoice Pending</option>
            <option value="PENDING_CEO_APPROVAL">CEO Pending</option>
            <option value="CEO_APPROVED">CEO Approved</option>
            <option value="PAYMENT_VERIFIED">Payment Verified</option>
            <option value="CORRECTION_REQUIRED">Correction</option>
          </select>

          <select className="input" value={paymentFilter} onChange={(e) => setPaymentFilter(e.target.value)}>
            <option value="ALL">All</option>
            <option value="OLD_INVOICE">Old Invoice Verify Pending</option>
            <option value="ACCOUNTANT_PENDING">Accountant Pending</option>
            <option value="CEO_PENDING">CEO Pending</option>
            <option value="PAYMENT_PENDING">Payment Pending</option>
            <option value="INVOICE_MISSING">Invoice Missing</option>
            <option value="BALANCE_PENDING">Balance Pending</option>
            <option value="BALANCE_CLEAR">Balance Clear</option>
          </select>

          <select className="input" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="NEWEST">Newest</option>
            <option value="OLDEST">Oldest</option>
            <option value="AMOUNT_HIGH">Amount High</option>
          </select>
        </div>
      </div>

      <div className="bg-white rounded-2xl border p-4">
        <div className="mb-4 text-center">
          <h2 className="text-lg font-black text-slate-900">
            Vendor Registration Fee Accounting Register
          </h2>
          <p className="text-xs text-slate-500">
            Invoice = Debit | Payment Receipt = Credit | Balance = Debit - Credit
          </p>
        </div>

        <div className="overflow-auto">
          <table className="min-w-[1780px] w-full text-[11px] border border-slate-300">
            <thead className="bg-slate-900 text-white">
              <tr>
                <th className="p-2 border text-center w-10">Sr</th>
                <th className="p-2 border text-left w-[280px]">Vendor</th>
                <th className="p-2 border text-center w-20">FY</th>
                <th className="p-2 border text-left w-[160px]">Invoice</th>
                <th className="p-2 border text-right w-24">Taxable</th>
                <th className="p-2 border text-right w-20">GST</th>
                <th className="p-2 border text-right w-24">Gross</th>
                <th className="p-2 border text-right w-24">Debit</th>
                <th className="p-2 border text-right w-24">Credit</th>
                <th className="p-2 border text-right w-24">Balance</th>
                <th className="p-2 border text-center w-28">Accountant</th>
                <th className="p-2 border text-center w-28">CEO</th>
                <th className="p-2 border text-center w-24">Invoice PDF</th>
                <th className="p-2 border text-center w-28">Payment Receipt</th>
                <th className="p-2 border text-center w-28">Payment</th>
                <th className="p-2 border text-center w-[260px]">Action</th>
              </tr>
            </thead>

            <tbody>
              {rows.map((v, i) => (
                <tr key={v.id} className="hover:bg-slate-50">
                  <td className="p-2 border text-center">{i + 1}</td>

                  <td className="p-2 border">
                    <div className="font-black text-slate-900">{v.vendorDisplay}</div>
                    <div className="text-[10px] text-slate-500">{v.emailDisplay}</div>
                    <div className="text-[10px] text-slate-500">{v.mobileDisplay}</div>
                    <div className="text-[10px] text-slate-400">UID: {v.id}</div>
                  </td>

                  <td className="p-2 border text-center font-black">FY {v.invoiceFY || "—"}</td>

                  <td className="p-2 border font-black">
                    {v.registrationInvoiceNo || "—"}
                    <div className="text-[10px] text-slate-500">
                      {fmtDate(v.registrationInvoiceDate)}
                    </div>
                    {v.hasInvoice && !v.oldInvoiceVerified && (
                      <div className="text-[10px] text-purple-700">
                        OLD VERIFY PENDING
                      </div>
                    )}
                  </td>

                  <td className="p-2 border text-right">{INR(v.taxableAmount)}</td>
                  <td className="p-2 border text-right">{INR(v.gstAmount)}</td>
                  <td className="p-2 border text-right font-black">{INR(v.amount)}</td>

                  <td className="p-2 border text-right text-red-600 font-black">
                    {v.invoiceDebit ? INR(v.invoiceDebit) : "—"}
                  </td>

                  <td className="p-2 border text-right text-emerald-700 font-black">
                    {v.paymentCredit ? INR(v.paymentCredit) : "—"}
                  </td>

                  <td className="p-2 border text-right font-black">
                    {INR(v.balance)}
                  </td>

                  <td className="p-2 border text-center">
                    <Badge>{v.accountantVerified ? "VERIFIED" : "PENDING"}</Badge>
                  </td>

                  <td className="p-2 border text-center">
                    <Badge>{v.ceoVerified ? "CEO_APPROVED" : "PENDING"}</Badge>
                  </td>

                  <td className="p-2 border text-center">
                    {v.registrationInvoicePdfUrl ? (
                      <button className="btn-gray" onClick={() => window.open(v.registrationInvoicePdfUrl, "_blank")}>
                        Invoice
                      </button>
                    ) : (
                      "—"
                    )}
                  </td>

                  <td className="p-2 border text-center">
                    {v.registrationPaymentReceiptPdfUrl ? (
                      <button
                        className="btn-gray"
                        onClick={() => window.open(v.registrationPaymentReceiptPdfUrl, "_blank")}
                      >
                        Receipt
                      </button>
                    ) : (
                      "—"
                    )}
                  </td>

                  <td className="p-2 border text-center">
                    <Badge>{v.paymentStatus}</Badge>
                    {v.registrationPaymentUtr && (
                      <div className="text-[10px] text-slate-500">
                        {v.registrationPaymentUtr}
                      </div>
                    )}
                  </td>

                  <td className="p-2 border">
                    <div className="flex flex-wrap gap-2 justify-center">
                      <button className="btn-red" onClick={() => sendCorrection(v)}>
                        Correction
                      </button>

                      {me.role === "ACCOUNTANT" && (
                        <button className="btn-gray" onClick={() => editInvoiceDetails(v)}>
                          Edit
                        </button>
                      )}

                      {me.role === "ACCOUNTANT" && v.hasInvoice && !v.accountantVerified && (
                        <button className="btn-dark" onClick={() => verifyOldInvoice(v)}>
                          Verify Old
                        </button>
                      )}

                      {me.role === "ACCOUNTANT" && !v.registrationInvoiceNo && (
                        <button className="btn-dark" onClick={() => accountantGenerateInvoice(v)}>
                          Gen Invoice
                        </button>
                      )}

                      {me.role === "CEO" && v.accountantVerified && !v.ceoVerified && (
                        <button className="btn" onClick={() => ceoApproveInvoice(v)}>
                          CEO Approve
                        </button>
                      )}

                      {me.role === "ACCOUNTANT" &&
                        v.registrationInvoiceStatus === "CEO_APPROVED" &&
                        v.paymentStatus !== "PAYMENT_VERIFIED" && (
                          <button className="btn" onClick={() => accountantVerifyPayment(v)}>
                            Verify Pay
                          </button>
                        )}
                    </div>
                  </td>
                </tr>
              ))}

              {!rows.length && (
                <tr>
                  <td colSpan={16} className="p-8 text-center text-slate-500">
                    No vendor registration records found.
                  </td>
                </tr>
              )}
            </tbody>

            {rows.length > 0 && (
              <tfoot className="bg-slate-100 font-black">
                <tr>
                  <td colSpan={4} className="p-2 border text-right">
                    Total
                  </td>
                  <td className="p-2 border text-right">
                    {INR(rows.reduce((s, v) => s + Number(v.taxableAmount || 0), 0))}
                  </td>
                  <td className="p-2 border text-right">
                    {INR(rows.reduce((s, v) => s + Number(v.gstAmount || 0), 0))}
                  </td>
                  <td className="p-2 border text-right">
                    {INR(rows.reduce((s, v) => s + Number(v.amount || 0), 0))}
                  </td>
                  <td className="p-2 border text-right text-red-600">
                    {INR(totals.debit)}
                  </td>
                  <td className="p-2 border text-right text-emerald-700">
                    {INR(totals.credit)}
                  </td>
                  <td className="p-2 border text-right">
                    {INR(totals.balance)}
                  </td>
                  <td colSpan={6} className="p-2 border"></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      <style>{`
        .input{
          border:1px solid #cbd5e1;
          border-radius:12px;
          padding:9px 12px;
          font-size:12px;
          font-weight:700;
          width:100%;
        }
        .btn{
          background:#059669;
          color:white;
          padding:7px 10px;
          border-radius:9px;
          font-size:10px;
          font-weight:900;
        }
        .btn-dark{
          background:#0f172a;
          color:white;
          padding:7px 10px;
          border-radius:9px;
          font-size:10px;
          font-weight:900;
        }
        .btn-red{
          background:#dc2626;
          color:white;
          padding:7px 10px;
          border-radius:9px;
          font-size:10px;
          font-weight:900;
        }
        .btn-gray{
          background:#e2e8f0;
          color:#0f172a;
          padding:7px 10px;
          border-radius:9px;
          font-size:10px;
          font-weight:900;
        }
      `}</style>
    </div>
  );
}

function Card({ title, value, green, red }) {
  return (
    <div className="bg-white rounded-2xl border p-4 shadow-sm">
      <p className="text-xs text-slate-500">{title}</p>
      <h2
        className={`text-lg font-black mt-2 ${
          green ? "text-emerald-700" : red ? "text-red-600" : "text-slate-900"
        }`}
      >
        {value}
      </h2>
    </div>
  );
}